import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Crown, 
  Clock, 
  ClipboardList, 
  Map, 
  Sparkles,
  CheckCircle2,
  ArrowRight,
  Zap,
  FileText
} from "lucide-react";

export function UpgradeModal({ 
  open, 
  onOpenChange, 
  reason = "generic", // "time_limit", "practice_tests", "learning_path", "generic"
  currentPlan = "starter",
  testsUsed = 0,
  testsLimit = 1,
  timeUsed = 0,
  timeLimit = 60
}) {
  const navigate = useNavigate();

  const getReasonContent = () => {
    switch (reason) {
      case "time_limit":
        return {
          icon: <Clock className="w-8 h-8 text-amber-500" />,
          title: "Daily Learning Limit Reached",
          subtitle: `You've used ${Math.round(timeUsed / 60)} of ${Math.round(timeLimit / 60)} hours today`,
          description: "Upgrade your plan to unlock more learning time and keep the momentum going!",
          features: [
            { plan: "Plus", benefit: "2 hours daily", highlight: false },
            { plan: "Pro", benefit: "4 hours daily", highlight: true }
          ]
        };
      case "practice_tests":
        return {
          icon: <ClipboardList className="w-8 h-8 text-blue-500" />,
          title: "Practice Test Limit Reached",
          subtitle: `You've taken ${testsUsed} of ${testsLimit} test${testsLimit > 1 ? 's' : ''} today`,
          description: "Want to practice more? Upgrade to take more practice tests and boost your scores!",
          features: [
            { plan: "Plus", benefit: "3 tests/day", highlight: false },
            { plan: "Pro", benefit: "Unlimited tests", highlight: true }
          ]
        };
      case "learning_path":
        return {
          icon: <Map className="w-8 h-8 text-purple-500" />,
          title: "Unlock Learning Path",
          subtitle: "See your personalized learning journey",
          description: "Track your progress through concepts, see what's next, and stay on track with guided learning!",
          features: [
            { plan: "Plus", benefit: "Current concept view", highlight: false },
            { plan: "Pro", benefit: "Full path + recommendations", highlight: true }
          ]
        };
      default:
        return {
          icon: <Sparkles className="w-8 h-8 text-emerald-500" />,
          title: "Unlock Premium Features",
          subtitle: "Get more out of Eduzy",
          description: "Upgrade to access practice tests, learning paths, progress reports, and more!",
          features: [
            { plan: "Plus", benefit: "Core features", highlight: false },
            { plan: "Pro", benefit: "All features", highlight: true }
          ]
        };
    }
  };

  const content = getReasonContent();

  const planPricing = {
    starter: { price: 499, name: "Starter" },
    plus: { price: 699, name: "Plus" },
    pro: { price: 899, name: "Pro" }
  };

  const handleUpgrade = (plan) => {
    onOpenChange(false);
    // Navigate to pricing with the selected plan pre-selected
    navigate(`/pricing?plan=${plan.toLowerCase()}`);
  };

  const handleComparePlans = () => {
    // Open pricing page in a new tab to keep current session secure
    window.open("/eduwise", "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mb-4">
            {content.icon}
          </div>
          <DialogTitle className="text-xl">{content.title}</DialogTitle>
          <DialogDescription className="space-y-2">
            <span className="block font-medium text-gray-700">{content.subtitle}</span>
            <span className="block text-sm">{content.description}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Plan Options - Clickable Cards */}
        <div className="space-y-3 py-4">
          {content.features.map((feature, idx) => (
            <div 
              key={idx}
              onClick={() => handleUpgrade(feature.plan)}
              className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-lg ${
                feature.highlight 
                  ? "bg-gradient-to-r from-emerald-500 to-teal-500 border-emerald-400 text-white hover:from-emerald-600 hover:to-teal-600" 
                  : "bg-gradient-to-r from-blue-500 to-indigo-500 border-blue-400 text-white hover:from-blue-600 hover:to-indigo-600"
              }`}
              data-testid={`plan-option-${feature.plan.toLowerCase()}`}
            >
              <div className="flex items-center gap-3">
                {feature.highlight ? (
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Crown className="w-5 h-5" />
                  </div>
                ) : (
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                )}
                <div>
                  <span className="font-bold text-lg">{feature.plan}</span>
                  <p className="text-sm opacity-90">{feature.benefit}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {feature.highlight && (
                  <Badge className="bg-white/20 text-white border-0 text-xs">Best Value</Badge>
                )}
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          ))}
        </div>

        {/* Current Plan Indicator */}
        {currentPlan && (
          <div className="text-center text-sm text-gray-500">
            Current plan: <span className="font-medium">{planPricing[currentPlan]?.name || currentPlan}</span>
          </div>
        )}

        <DialogFooter className="flex flex-col gap-2 sm:flex-col">
          <Button
            onClick={handleComparePlans}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
            data-testid="compare-plans-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Compare All Plans
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default UpgradeModal;
